/**
 * The deploying view's component table: every deployable component, what this release
 * changes in it, where it stands, and the actions for it. A plain MUI `Table` — about
 * twenty rows, always all of them, so a data grid's virtualization, filtering and
 * column machinery would be weight without a job.
 *
 * The table lists components the release does not touch as well as the ones it does,
 * because "receipts is not in this release" is the answer to a question operators ask
 * about it, and because a deliberate no-change redeploy starts from that row. Order is
 * the registry's deploy order, which is advice: what to do next reads down the page,
 * and going out of order is allowed but says so first.
 */
import { Fragment, useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
    DEPLOY_GROUP_RATIONALE,
    type Acknowledgement,
    type ChangeSetPointer,
    type HostMachine,
    type Phase,
} from '@finvari/deployer';
import {
    DEPLOY_KIND_LABEL,
    changeSummary,
    compileOnlyExplanation,
    isBusy,
    stateChip,
    type ComponentRow,
} from '../lib/componentRows';
import type { InstructionRow } from '../lib/instructionActivity';
import { agoTextCoarse } from '../lib/hostStatus';
import { ComponentActions } from './ComponentActions';
import { ComponentDetail } from './ComponentDetail';

export interface ComponentTableProps {
    version: string;
    rows: ComponentRow[];
    /** This release's instructions on the release host, newest first. */
    instructions: InstructionRow[];
    /** The release-wide change set, for the detail panel's Changes tab. */
    changes: ChangeSetPointer | undefined;
    phase: Phase;
    now: number;
    /**
     * The release host's hardware, which bounds how many components may build at
     * once. Passed down rather than read here so the table stays a pure render of
     * what it is given.
     */
    machine?: HostMachine;
}

export function ComponentTable({ version, rows, instructions, changes, phase, now, machine }: ComponentTableProps) {
    // One open row at a time: the detail panel is tall (diffs, a terminal), and two
    // expanded rows push the second one's content off the screen anyway.
    const [open, setOpen] = useState<string | undefined>(undefined);

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Component</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Last deployed</TableCell>
                    <TableCell>This release</TableCell>
                    <TableCell align="right">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <Fragment key={row.name}>
                        <ComponentRowView
                            row={row}
                            rows={rows}
                            version={version}
                            phase={phase}
                            now={now}
                            open={open === row.name}
                            onToggle={() => setOpen((current) => (current === row.name ? undefined : row.name))}
                            machine={machine}
                        />
                        <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                                <Collapse in={open === row.name} unmountOnExit>
                                    <Box sx={{ px: 2, py: 2, bgcolor: 'grey.50' }}>
                                        <ComponentDetail
                                            row={row}
                                            version={version}
                                            changes={changes}
                                            instructions={instructions}
                                            now={now}
                                        />
                                    </Box>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </Fragment>
                ))}
            </TableBody>
        </Table>
    );
}

/** States that are context rather than work this release is waiting on. */
const DIM_STATES = new Set<ComponentRow['state']>(['unchanged', 'compileOnly', 'upToDate']);

function ComponentRowView({
    row,
    rows,
    version,
    phase,
    now,
    open,
    onToggle,
    machine,
}: {
    row: ComponentRow;
    rows: ComponentRow[];
    version: string;
    phase: Phase;
    now: number;
    open: boolean;
    onToggle: () => void;
    machine?: HostMachine;
}) {
    const chip = stateChip(row);
    return (
        <TableRow
            hover
            // The rows that need something are the reason the page is open; a component
            // the release does not touch is context, and reads as context. A
            // compile-only row is context too — it still has to build, but nothing is
            // waiting on it, and at full strength it would read as outstanding work.
            // So is an up-to-date one: an earlier release deployed it, and this one
            // has nothing to do there.
            sx={{
                '& td': { borderBottom: 0 },
                opacity: DIM_STATES.has(row.state) && !isBusy(row) ? 0.6 : 1,
            }}
        >
            <TableCell>
                <IconButton size="small" onClick={onToggle} aria-label={`${open ? 'Hide' : 'Show'} ${row.name} detail`}>
                    {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
            </TableCell>
            <TableCell>
                <Typography variant="body2">{row.name}</Typography>
                {row.deployKind !== 'firebase' && (
                    <Typography variant="caption" color="text.secondary">
                        {DEPLOY_KIND_LABEL[row.deployKind]}
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <Tooltip title={row.state === 'compileOnly' ? compileOnlyExplanation(row) : ''}>
                    <Typography
                        variant="body2"
                        color={row.changeKind === 'none' || row.changeKind === 'compile-only' ? 'text.secondary' : 'text.primary'}
                    >
                        {changeSummary(row)}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Tooltip title={DEPLOY_GROUP_RATIONALE[row.deployGroup] ?? ''}>
                    <Chip size="small" variant="outlined" label={row.deployGroup} />
                </Tooltip>
            </TableCell>
            <TableCell>
                <LastDeployed row={row} now={now} />
            </TableCell>
            <TableCell>
                <Chip size="small" color={chip.color} label={chip.label} />
                {row.acknowledgement && row.state === 'acknowledged' && (
                    <Tooltip title={skipTooltip(row.acknowledgement, now)}>
                        {/*
                          * As much of the reason as fits, clipped by CSS rather than a
                          * slice so the cut lands on the pixel rather than a character
                          * count. `maxWidth` because an auto-layout table would
                          * otherwise widen the column to hold the whole nowrap line.
                          */}
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block', maxWidth: 180 }}
                        >
                            {row.acknowledgement.reason}
                        </Typography>
                    </Tooltip>
                )}
                {/* Not on a deployed or up-to-date row: the compile is implied by the
                    deploy that put the commit live, so saying it adds nothing. */}
                {row.compiled && row.state !== 'deployed' && row.state !== 'upToDate' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        compiled
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right">
                <ComponentActions row={row} rows={rows} version={version} phase={phase} machine={machine} />
            </TableCell>
        </TableRow>
    );
}

/** The whole reason, and who owns the decision, for the caption that shows part of it. */
function skipTooltip(ack: Acknowledgement, now: number): string {
    const at = ack.at?.toMillis?.();
    const when = at === undefined ? '' : ` ${agoTextCoarse(Math.max(0, now - at))}`;
    return `Skipped by ${ack.by.email}${when}: ${ack.reason}`;
}

/**
 * Across releases, not this one: a component nothing has deployed for two months is
 * a candidate for a no-change redeploy, and that is only visible if the column keeps
 * showing the last one whatever this release does.
 */
function LastDeployed({ row, now }: { row: ComponentRow; now: number }) {
    if (!row.lastDeployed) {
        // The marker is a deploy too — the laptop's, during the interop window — and
        // reporting "never" over one would be plainly wrong to whoever ran it.
        if (row.deployMarker) {
            return (
                <Tooltip title={`deploy marker says ${row.deployMarker.at}`}>
                    <Typography variant="caption" color="text.secondary">
                        {row.deployMarker.commit} (not through this system)
                    </Typography>
                </Tooltip>
            );
        }
        return (
            <Typography variant="caption" color="text.secondary">
                no record
            </Typography>
        );
    }
    const at = row.lastDeployed.at?.toMillis?.();
    return (
        <Stack spacing={0}>
            <Typography variant="body2">{row.lastDeployed.version}</Typography>
            <Typography variant="caption" color="text.secondary">
                {at === undefined ? row.lastDeployed.commit.slice(0, 7) : agoTextCoarse(Math.max(0, now - at))}
            </Typography>
        </Stack>
    );
}
